export type MetricFormat = 'currency' | 'currency2dec' | 'integer' | 'percent'

export interface AttributesFilterState {
  planId: string | null
  seasonCalendarId: string | null
  apStatus: string | null
  departmentId: string | null
  classId: string | null
  subclassId: string | null
  brand: string | null
}

export type AggregateBy =
  | 'brand_name'
  | 'city_shipping_from'
  | 'class'
  | 'conveyable'
  | 'country_of_origin'
  | 'custom_attributes'
  | 'department'
  | 'editable_check'
  | 'exclusive_design'
  | 'factory_id'
  | 'factory_name'
  | 'is_new_sku'
  | 'item_type'
  | 'lifestyle'
  | 'subclass'
  | 'vendor'

export const AGGREGATE_OPTIONS: { value: AggregateBy; label: string }[] = [
  { value: 'brand_name', label: 'Brand Name' },
  { value: 'city_shipping_from', label: 'City Shipping From' },
  { value: 'class', label: 'Class' },
  { value: 'conveyable', label: 'Conveyable' },
  { value: 'country_of_origin', label: 'Country of Origin' },
  { value: 'custom_attributes', label: 'Custom Attributes' },
  { value: 'department', label: 'Department' },
  { value: 'editable_check', label: 'Editable check' },
  { value: 'exclusive_design', label: 'Exclusive Design' },
  { value: 'factory_id', label: 'Factory ID' },
  { value: 'factory_name', label: 'Factory Name' },
  { value: 'is_new_sku', label: 'Is New SKU' },
  { value: 'item_type', label: 'Item Type' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'subclass', label: 'Subclass' },
  { value: 'vendor', label: 'Vendor' },
]

const BUCKETS_BY_AGGREGATE: Record<AggregateBy, string[]> = {
  brand_name: [
    'Studio Active',
    'CORE',
    'Non-Private Brand',
    'N/A',
    'Northbound',
    'Atelier',
    'Coastline',
    'EVERWEAR',
    'EmberRun',
    'Field & Pine',
    'Heritage Co.',
    'Loft Studio',
    'Ashbury',
    'Brand Name',
    'Sunday Best',
  ],
  city_shipping_from: [
    'Plano, TX',
    'Memphis, TN',
    'Charlotte, NC',
    'Sacramento, CA',
    'Toronto, ON',
    'Reno, NV',
    'Atlanta, GA',
  ],
  class: [
    'Tees & Tanks',
    'Jeans & Denim',
    'Outerwear',
    'Dresses',
    'Sweaters & Knits',
    'Shirts & Blouses',
    'Activewear',
    'Shorts',
    'Pants & Chinos',
    'Loungewear',
  ],
  conveyable: ['Conveyable', 'Non-Conveyable'],
  country_of_origin: [
    'China',
    'India',
    'Vietnam',
    'USA',
    'Mexico',
    'Thailand',
    'Bangladesh',
    'Cambodia',
    'Pakistan',
  ],
  custom_attributes: ['Premium', 'Core', 'Essentials', 'Limited', 'Other'],
  department: [
    'Mens',
    'Womens',
    'Kids',
    'Activewear',
    'Denim',
    'Outerwear',
    'Basics',
    'Intimates',
  ],
  editable_check: ['Editable', 'Non-Editable'],
  exclusive_design: ['Exclusive', 'Non-Exclusive'],
  factory_id: [
    'FAC-001',
    'FAC-014',
    'FAC-027',
    'FAC-082',
    'FAC-119',
    'FAC-204',
    'FAC-318',
    'FAC-455',
  ],
  factory_name: [
    'Hangzhou Apparel Co.',
    'Mumbai Textile Mfg.',
    'Hanoi Garment Industries',
    'Texas Apparel Inc.',
    'Guadalajara Garments',
    'Bangkok Apparel',
    'Dhaka Textile',
  ],
  is_new_sku: ['New SKU', 'Existing'],
  item_type: ['Tops', 'Bottoms', 'Outerwear', 'Accessories'],
  lifestyle: [
    'Classic',
    'Casual',
    'Athleisure',
    'Streetwear',
    'Workwear',
    'Loungewear',
  ],
  subclass: [
    'Basic Tees',
    'Graphic Tees',
    'Pullovers',
    'Skinny Jeans',
    'Joggers',
    'Polos',
    'Cardigans',
    'Puffers',
    'Boxer Briefs',
    'Crew Socks',
  ],
  vendor: [
    'Vendor 100',
    'Vendor 215',
    'Vendor 328',
    'Vendor 441',
    'Vendor 506',
    'Vendor 619',
    'Vendor 732',
  ],
}

interface AttributesRowBase {
  label: string
  totalSales: number
  totalUnits: number
  skuCount: number
  regPromoSales: number
  clearanceSales: number
  aur: number
  auc: number
  tariffAmount: number
  cost: number
  gm: number
  gmPct: number
  discountPct: number
  retail: number
  landedCost: number
  fobCost: number
  purchaseUnits: number
  imuPct: number
  recAur: number
}

// Brand Name baseline — these numbers match the screenshot when every filter is "All".
const BRAND_BASE_ROWS: AttributesRowBase[] = [
  {
    label: 'Studio Active',
    totalSales: 22278453,
    totalUnits: 840655,
    skuCount: 75,
    regPromoSales: 22127645,
    clearanceSales: 1109856,
    aur: 27.69,
    auc: 0.9,
    tariffAmount: 0,
    cost: 3664,
    gm: 2996171,
    gmPct: 67.1,
    discountPct: 47.44,
    retail: 16897317.96,
    landedCost: 1465246.12,
    fobCost: 1103689.69,
    purchaseUnits: 1632673,
    imuPct: 93.47,
    recAur: 10.41,
  },
  {
    label: 'CORE',
    totalSales: 3171273,
    totalUnits: 159566,
    skuCount: 72,
    regPromoSales: 3076135,
    clearanceSales: 95138,
    aur: 19.87,
    auc: 0.0,
    tariffAmount: 0,
    cost: 17866,
    gm: 1585448.03,
    gmPct: 49.57,
    discountPct: 51.0,
    retail: 1585825.45,
    landedCost: 1369715.64,
    fobCost: 1300540.64,
    purchaseUnits: 46426,
    imuPct: 49.57,
    recAur: 3.36,
  },
  {
    label: 'Non-Private Brand',
    totalSales: 2493634,
    totalUnits: 683227,
    skuCount: 107,
    regPromoSales: 2448635,
    clearanceSales: 44999,
    aur: 3.65,
    auc: 0.1,
    tariffAmount: 0,
    cost: 14371,
    gm: 1226772.25,
    gmPct: 50.8,
    discountPct: 49.3,
    retail: 1266861.55,
    landedCost: 1131737.97,
    fobCost: 1204791.42,
    purchaseUnits: 741092,
    imuPct: 50.8,
    recAur: 3.48,
  },
  {
    label: 'N/A',
    totalSales: 1371099,
    totalUnits: 240701066,
    skuCount: 13708,
    regPromoSales: 1121745,
    clearanceSales: 48397,
    aur: 0.2,
    auc: 0.0,
    tariffAmount: 0,
    cost: 14307,
    gm: -1874223.22,
    gmPct: 80.0,
    discountPct: 136.7,
    retail: -1874223.22,
    landedCost: -336934.67,
    fobCost: -273346.08,
    purchaseUnits: -551105621662,
    imuPct: 0.0,
    recAur: 0.0,
  },
  {
    label: 'Northbound',
    totalSales: 190004,
    totalUnits: 12504,
    skuCount: 565,
    regPromoSales: 190004,
    clearanceSales: 0,
    aur: 15.2,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 187964.0,
    gmPct: 68.1,
    discountPct: 45.04,
    retail: 402040.37,
    landedCost: 211931.0,
    fobCost: 57682.0,
    purchaseUnits: 24,
    imuPct: 82.54,
    recAur: 21.04,
  },
  {
    label: 'Atelier',
    totalSales: 220985,
    totalUnits: 4000,
    skuCount: 1194,
    regPromoSales: 209940,
    clearanceSales: 11049,
    aur: 55.25,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 50000.0,
    gmPct: 77.4,
    discountPct: 11.82,
    retail: 220985.0,
    landedCost: 41200.0,
    fobCost: 2040.0,
    purchaseUnits: 4220,
    imuPct: 80.0,
    recAur: 0.0,
  },
  {
    label: 'Coastline',
    totalSales: 188000,
    totalUnits: 2000,
    skuCount: 1209,
    regPromoSales: 179600,
    clearanceSales: 8300,
    aur: 94.2,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 40000.0,
    gmPct: 78.7,
    discountPct: 4.41,
    retail: 188000.0,
    landedCost: 41200.0,
    fobCost: 2060.0,
    purchaseUnits: 80,
    imuPct: 80.0,
    recAur: 23.39,
  },
  {
    label: 'EVERWEAR',
    totalSales: 115602,
    totalUnits: 18648,
    skuCount: 1,
    regPromoSales: 84758,
    clearanceSales: 4701,
    aur: 7.49,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 84646.4,
    gmPct: 53.7,
    discountPct: 70.1,
    retail: 80303.52,
    landedCost: 33113.34,
    fobCost: 23634.4,
    purchaseUnits: 23634,
    imuPct: 58.91,
    recAur: 0.84,
  },
  {
    label: 'EmberRun',
    totalSales: 54000,
    totalUnits: 100,
    skuCount: 1,
    regPromoSales: 54000,
    clearanceSales: 0,
    aur: 34.0,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 89300.0,
    gmPct: 78.7,
    discountPct: 78.5,
    retail: 89300.0,
    landedCost: 41200.0,
    fobCost: 2060.0,
    purchaseUnits: 2060,
    imuPct: 80.0,
    recAur: 0.0,
  },
  {
    label: 'Field & Pine',
    totalSales: 90500,
    totalUnits: 1000,
    skuCount: 3,
    regPromoSales: 85975,
    clearanceSales: 4525,
    aur: 30.5,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 51000.0,
    gmPct: 43.6,
    discountPct: 7.4,
    retail: 367617.2,
    landedCost: 71872.54,
    fobCost: 69156.0,
    purchaseUnits: 3784,
    imuPct: 80.88,
    recAur: 95.23,
  },
  {
    label: 'Heritage Co.',
    totalSales: 1000,
    totalUnits: 1000,
    skuCount: 583,
    regPromoSales: 0,
    clearanceSales: 0,
    aur: 1.0,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 1000.0,
    gmPct: 0.0,
    discountPct: 100.0,
    retail: 1.0,
    landedCost: 5.0,
    fobCost: 8.0,
    purchaseUnits: 9,
    imuPct: 0.0,
    recAur: 0.0,
  },
  {
    label: 'Loft Studio',
    totalSales: 0,
    totalUnits: 0,
    skuCount: 0,
    regPromoSales: 0,
    clearanceSales: 0,
    aur: 0.0,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 0.0,
    gmPct: 0.0,
    discountPct: 0.0,
    retail: 0.0,
    landedCost: 0.0,
    fobCost: 0.0,
    purchaseUnits: 0,
    imuPct: 0.0,
    recAur: 0.0,
  },
  {
    label: 'Ashbury',
    totalSales: 0,
    totalUnits: 1894265,
    skuCount: 66,
    regPromoSales: 0,
    clearanceSales: 0,
    aur: 0.0,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: -22916600.42,
    gmPct: 0.0,
    discountPct: 0.0,
    retail: 0.0,
    landedCost: 0.0,
    fobCost: 0.0,
    purchaseUnits: 0,
    imuPct: -22.01,
    recAur: 0.0,
  },
  {
    label: 'Brand Name',
    totalSales: 0,
    totalUnits: 0,
    skuCount: 0,
    regPromoSales: 0,
    clearanceSales: 0,
    aur: 0.0,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 0.0,
    gmPct: 0.0,
    discountPct: 0.0,
    retail: 0.0,
    landedCost: 0.0,
    fobCost: 0.0,
    purchaseUnits: 0,
    imuPct: 0.0,
    recAur: 0.0,
  },
  {
    label: 'Sunday Best',
    totalSales: 0,
    totalUnits: 0,
    skuCount: 1,
    regPromoSales: 0,
    clearanceSales: 0,
    aur: 0.0,
    auc: 0.0,
    tariffAmount: 0,
    cost: 0,
    gm: 0.0,
    gmPct: 0.0,
    discountPct: 0.0,
    retail: 0.0,
    landedCost: 0.0,
    fobCost: 0.0,
    purchaseUnits: 42834,
    imuPct: 0.0,
    recAur: 0.0,
  },
]

// Aggregate row matching the screenshot total — anchors the "All" baseline.
const BRAND_TOTAL: AttributesRowBase = {
  label: 'Total',
  totalSales: 68971001,
  totalUnits: 244604954, // (240701066 + many) - approx but close to design
  skuCount: 6454,
  regPromoSales: 65005264,
  clearanceSales: 3193438,
  aur: 0.0,
  auc: 0.0,
  tariffAmount: 0,
  cost: 4152525,
  gm: 18002623.89,
  gmPct: 72.7,
  discountPct: 42.41,
  retail: 95948139.17,
  landedCost: 5103237259.17,
  fobCost: 19781656.2,
  purchaseUnits: 5510562311662,
  imuPct: 79.38,
  recAur: 0.0,
}

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

function shrinkForFilter(filterValue: string, filterName: string): number {
  const seed = hashString(filterValue)
  const n = normalizedFromHash(seed, filterName)
  return 0.4 + n * 0.35
}

function activeFilterPairs(
  filters: AttributesFilterState
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.planId) pairs.push(['plan', filters.planId])
  if (filters.seasonCalendarId)
    pairs.push(['seasonCal', filters.seasonCalendarId])
  if (filters.apStatus) pairs.push(['status', filters.apStatus])
  if (filters.departmentId) pairs.push(['dept', filters.departmentId])
  if (filters.classId) pairs.push(['class', filters.classId])
  if (filters.subclassId) pairs.push(['subclass', filters.subclassId])
  if (filters.brand) pairs.push(['brand', filters.brand])
  return pairs
}

const ABSOLUTE_KEYS: (keyof AttributesRowBase)[] = [
  'totalSales',
  'totalUnits',
  'skuCount',
  'regPromoSales',
  'clearanceSales',
  'tariffAmount',
  'cost',
  'gm',
  'retail',
  'landedCost',
  'fobCost',
  'purchaseUnits',
]

const RATIO_KEYS: (keyof AttributesRowBase)[] = [
  'aur',
  'auc',
  'gmPct',
  'discountPct',
  'imuPct',
  'recAur',
]

function shiftRow(
  row: AttributesRowBase,
  totalShrink: number,
  wiggleSeed: number,
  index: number
): AttributesRowBase {
  if (wiggleSeed === 0) return row
  const out: AttributesRowBase = { ...row }
  for (const key of ABSOLUTE_KEYS) {
    const base = row[key] as number
    const noise =
      (normalizedFromHash(wiggleSeed, `abs:${key}:${index}`) - 0.5) * 0.06
    ;(out[key] as number) = base * totalShrink * (1 + noise)
  }
  for (const key of RATIO_KEYS) {
    const base = row[key] as number
    const noise =
      (normalizedFromHash(wiggleSeed, `ratio:${key}:${index}`) - 0.5) * 0.1
    ;(out[key] as number) = base * (1 + noise)
  }
  return out
}

function rebucket(
  bucketLabels: string[],
  totalRow: AttributesRowBase,
  aggregateBy: AggregateBy
): AttributesRowBase[] {
  // Deterministic splits for each absolute metric across the bucket list.
  const weights = bucketLabels.map((label, i) => {
    const n = normalizedFromHash(hashString(`${aggregateBy}:${label}`), `w${i}`)
    return 0.2 + n * 0.8
  })
  const sumW = weights.reduce((a, b) => a + b, 0)
  const fractions = weights.map(w => w / sumW)

  return bucketLabels.map((label, i) => {
    const f = fractions[i]
    const ratioNoise = (k: string) =>
      (normalizedFromHash(hashString(`${aggregateBy}:${label}`), k) - 0.5) * 0.2
    return {
      label,
      totalSales: totalRow.totalSales * f,
      totalUnits: totalRow.totalUnits * f,
      skuCount: totalRow.skuCount * f,
      regPromoSales: totalRow.regPromoSales * f,
      clearanceSales: totalRow.clearanceSales * f,
      tariffAmount: totalRow.tariffAmount * f,
      cost: totalRow.cost * f,
      gm: totalRow.gm * f,
      retail: totalRow.retail * f,
      landedCost: totalRow.landedCost * f,
      fobCost: totalRow.fobCost * f,
      purchaseUnits: totalRow.purchaseUnits * f,
      // Ratios — nudge baselines a bit so buckets aren't identical.
      aur: 18 * (1 + ratioNoise('aur')),
      auc: 0.45 * (1 + ratioNoise('auc')),
      gmPct: totalRow.gmPct * (1 + ratioNoise('gmPct') * 0.5),
      discountPct: totalRow.discountPct * (1 + ratioNoise('discountPct') * 0.5),
      imuPct: totalRow.imuPct * (1 + ratioNoise('imuPct') * 0.5),
      recAur: 9 * (1 + ratioNoise('recAur')),
    }
  })
}

export interface AttributesResult {
  rows: AttributesRowBase[]
  total: AttributesRowBase
}

export function buildAttributesRows(
  aggregateBy: AggregateBy,
  filters: AttributesFilterState
): AttributesResult {
  const activePairs = activeFilterPairs(filters)
  const totalShrink = activePairs.reduce(
    (acc, [name, value]) => acc * shrinkForFilter(value, name),
    1
  )
  const wiggleSeed = activePairs.length
    ? hashString(activePairs.map(([n, v]) => `${n}=${v}`).join('|'))
    : 0

  const baseRows: AttributesRowBase[] =
    aggregateBy === 'brand_name'
      ? BRAND_BASE_ROWS
      : rebucket(BUCKETS_BY_AGGREGATE[aggregateBy], BRAND_TOTAL, aggregateBy)

  const rows = baseRows.map((r, i) => shiftRow(r, totalShrink, wiggleSeed, i))

  // Re-aggregate total from filtered rows so it stays internally consistent.
  const total: AttributesRowBase = {
    label: 'Total',
    totalSales: sum(rows, 'totalSales'),
    totalUnits: sum(rows, 'totalUnits'),
    skuCount: sum(rows, 'skuCount'),
    regPromoSales: sum(rows, 'regPromoSales'),
    clearanceSales: sum(rows, 'clearanceSales'),
    aur: weightedAvg(rows, 'aur', 'totalSales'),
    auc: weightedAvg(rows, 'auc', 'totalSales'),
    tariffAmount: sum(rows, 'tariffAmount'),
    cost: sum(rows, 'cost'),
    gm: sum(rows, 'gm'),
    gmPct: weightedAvg(rows, 'gmPct', 'totalSales'),
    discountPct: weightedAvg(rows, 'discountPct', 'totalSales'),
    retail: sum(rows, 'retail'),
    landedCost: sum(rows, 'landedCost'),
    fobCost: sum(rows, 'fobCost'),
    purchaseUnits: sum(rows, 'purchaseUnits'),
    imuPct: weightedAvg(rows, 'imuPct', 'totalSales'),
    recAur: weightedAvg(rows, 'recAur', 'totalSales'),
  }

  return { rows, total }
}

function sum(rows: AttributesRowBase[], key: keyof AttributesRowBase): number {
  return rows.reduce((acc, r) => acc + (r[key] as number), 0)
}

function weightedAvg(
  rows: AttributesRowBase[],
  key: keyof AttributesRowBase,
  weightKey: keyof AttributesRowBase
): number {
  let w = 0
  let v = 0
  for (const r of rows) {
    const wi = Math.abs(r[weightKey] as number)
    if (wi === 0) continue
    w += wi
    v += (r[key] as number) * wi
  }
  return w === 0 ? 0 : v / w
}

export function formatCurrency0(value: number): string {
  if (!isFinite(value)) return '$0'
  const abs = Math.abs(value)
  const formatted = abs.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
  return value < 0 ? `(${formatted})` : formatted
}

export function formatCurrency2(value: number): string {
  if (!isFinite(value)) return '$0.00'
  const abs = Math.abs(value)
  const formatted = abs.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return value < 0 ? `(${formatted})` : formatted
}

export function formatInteger(value: number): string {
  if (!isFinite(value)) return '0'
  return Math.round(value).toLocaleString('en-US')
}

export function formatPercent(value: number): string {
  if (!isFinite(value)) return '0%'
  return `${value.toFixed(2)}%`
}

export type { AttributesRowBase }
